import React, { useState, useEffect } from 'react';
import { Box, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button } from '@mui/material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import CashReport from './report_doc/CashReport';
import BankReport from './report_doc/BankReport';
import MathReport from './report_doc/MathReport';

interface TableField {
  cName: string;
  displayName: string;
  statisticsHide: number;
  hideInStatTable: number;
}

interface TableData {
  [key: string]: string | number;
}

const Statistics: React.FC = () => {
  const [tableFields, setTableFields] = useState<TableField[]>([]);
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth());
  const [pharmacyNum, setPharmacyNum] = useState<string | null>(null);
  const [userPhone, setUserPhone] = useState<string>('');
  const [fullName, setFullName] = useState<string>('');
  const [iin, setIin] = useState<string>('');

  useEffect(() => {
    document.title = 'Отчет по выручке | Zerde';
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      const username = localStorage.getItem('username');
      if (!username) return;

      try {
        const response = await axios.get('https://tbl.zerdegroup.kz/api/user-stitstic-info', {
          params: { username }
        });

        if (response.data.success) {
          setUserPhone(response.data.userphone || '');
          setFullName(response.data.fullName || '');
          setIin(response.data.iin || '');
          setPharmacyNum(response.data.pharmacyNum);
        }
      } catch (err) {
        console.error('Ошибка при получении данных пользователя:', err);
      }
    };

    fetchUserData();
  }, []);

  const isValidIIN = (iin: string): boolean => {
    const digits = iin.replace(/\s/g, '');
    return digits.length === 12 && /^\d+$/.test(digits);
  };

  const areReportsEnabled = () => {
    return fullName.trim() !== '' && userPhone.trim() !== '' && tableData.length > 0;
  };

  const areFieldsEnabled = () => {
    return iin.trim() !== '' && isValidIIN(iin);
  };

  function getCurrentMonth(): string {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  function getMonthsList(): { value: string; label: string }[] {
    const months = [];
    const currentDate = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleString('ru', { year: 'numeric', month: 'long' });
      months.push({ value, label });
    }
    return months;
  }

  function formatNumber(num: number | string | null | undefined): string {
    if (num === null || num === undefined) {
      return '';
    }

    if (typeof num === 'string') {
      num = parseFloat(num);
      if (isNaN(num)) {
        return '';
      }
    }

    return num.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  }

  useEffect(() => {
    const fetchTableStructure = async () => {
      try {
        const response = await axios.get('https://tbl.zerdegroup.kz/api/table-fields');
        let filteredFields = response.data.filter((field: TableField) => field.hideInStatTable === 0);

        if (!filteredFields.some((field: TableField) => field.cName === 'change_balance_end_of_day')) {
          filteredFields.push({
            cName: 'change_balance_end_of_day',
            displayName: 'Остаток мелочи на конец дня',
            statisticsHide: 0,
            hideInStatTable: 0
          });
        }

        setTableFields(filteredFields);
      } catch (error) {
        console.error('Ошибка при получении структуры таблицы:', error);
      }
    };
    fetchTableStructure();
  }, []);

  useEffect(() => {
    const fetchMonthData = async () => {
      const username = localStorage.getItem('username');
      if (!username) return;

      try {
        const response = await axios.get('https://tbl.zerdegroup.kz/api/statistics-data', {
          params: {
            username,
            month: selectedMonth
          }
        });

        const formattedData = response.data.map((row: TableData) => ({
          ...row,
          date: new Date(row.date).toLocaleDateString('ru-RU')
        }));

        setTableData(formattedData);
      } catch (error) {
        console.error('Ошибка при получении данных:', error);
      }
    };
    fetchMonthData();
  }, [selectedMonth]);

  const calculateTotals = () => {
    const totals: TableData = {};

    tableFields.forEach(field => {
      if (field.statisticsHide === 0 && field.cName !== 'date') {
        totals[field.cName] = tableData.reduce((sum, row) => {
          const value = Number(row[field.cName]) || 0;
          return sum + value;
        }, 0);
      }
    });

    return totals;
  };

  const exportToExcel = () => {
    const exportData = tableData.map(row => {
      const newRow: any = {};
      tableFields.forEach(field => {
        if (field.hideInStatTable === 0) {
          newRow[field.displayName] = row[field.cName];
        }
      });
      return newRow;
    });

    const totals = calculateTotals();
    const totalsRow: any = { 'Дата': 'Итого:' };
    tableFields.forEach(field => {
      if (field.hideInStatTable === 0 && field.cName !== 'date') {
        totalsRow[field.displayName] = totals[field.cName];
      }
    });
    exportData.push(totalsRow);

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    const columnWidths = tableFields
      .filter(field => field.hideInStatTable === 0)
      .map(field => ({ wch: Math.max(field.displayName.length, 15) }));
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Statistics");

    XLSX.writeFile(workbook, `Statistics_${selectedMonth}.xlsx`);
  };

  return (
    <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
        <Select
          size='small'
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          sx={{
            minWidth: 200,
            height: 40,
            bgcolor: 'white',
            borderRadius: '8px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e4e4e7'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d4d4d8'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#09090b'
            }
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                borderRadius: '8px',
                mt: 1,
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }
            }
          }}
        >
          {getMonthsList().map((month) => (
            <MenuItem key={month.value} value={month.value} sx={{ fontSize: '0.9rem' }}>
              {month.label}
            </MenuItem>
          ))}
        </Select>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={exportToExcel}
            disabled={!areReportsEnabled() || !areFieldsEnabled()}
            disableElevation
            sx={{
              bgcolor: '#10b981', // green-500
              color: 'white',
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: '8px',
              '&:hover': {
                bgcolor: '#059669'
              },
              '&:disabled': {
                bgcolor: '#e4e4e7',
                color: '#a1a1aa'
              }
            }}
          >
            EXCEL
          </Button>
          <CashReport
            tableData={tableData}
            pharmacyNum={pharmacyNum}
            startDate=""
            endDate=""
            selectedMonth={selectedMonth}
            employeeName={fullName}
            disabled={!areFieldsEnabled()}
          />
          <BankReport
            tableData={tableData}
            pharmacyNum={pharmacyNum}
            startDate=""
            endDate=""
            selectedMonth={selectedMonth}
            employeeName={fullName}
            disabled={!areFieldsEnabled()}
          />
          <MathReport
            tableData={tableData}
            pharmacyNum={pharmacyNum}
            username={localStorage.getItem('username')}
            selectedMonth={selectedMonth}
            employeeName={fullName}
            disabled={!areFieldsEnabled()}
          />
        </Box>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{
        minWidth: 650,
        maxHeight: '730px',
        border: '1px solid #e4e4e7',
        borderRadius: '12px'
      }}>
        <Table size="small" stickyHeader sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <TableHead>
            <TableRow>
              {tableFields.map((field) => (
                <TableCell
                  key={field.cName}
                  sx={{
                    whiteSpace: 'nowrap',
                    backgroundColor: 'white',
                    color: '#09090b',
                    fontWeight: 600,
                    borderBottom: '1px solid #e4e4e7',
                    fontSize: '0.9rem',
                    py: 1.5,
                    px: 2,
                    zIndex: 2,
                    letterSpacing: '-0.01em'
                  }}
                >
                  {field.displayName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  bgcolor: index % 2 === 0 ? 'white' : '#fafafa', // Subtle zebra striping
                  '&:hover': { bgcolor: '#f4f4f5' },
                  transition: 'background-color 0.15s'
                }}
              >
                {tableFields.map((field) => (
                  <TableCell
                    key={field.cName}
                    sx={{
                      whiteSpace: 'nowrap',
                      borderBottom: '1px solid #f4f4f5',
                      color: '#09090b',
                      fontSize: '0.9rem',
                      fontWeight: field.cName === 'date' ? 600 : 400,
                      py: 1.25,
                      px: 2,
                      fontFamily: field.cName !== 'date' ? '"Geist Mono", "Roboto Mono", monospace' : 'inherit',
                      ...(field.cName === 'date' ? {
                        position: 'sticky',
                        left: 0,
                        backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                        zIndex: 1,
                        borderRight: '1px solid #e4e4e7',
                      } : {})
                    }}
                  >
                    {field.cName === 'date' ? row[field.cName] : formatNumber(row[field.cName])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow sx={{
              position: 'sticky',
              bottom: 0,
              bgcolor: '#f4f4f5',
              '& td': { borderTop: '2px solid #e4e4e7' } // Stronger border for total
            }}>
              <TableCell sx={{
                position: 'sticky',
                bottom: 0,
                left: 0,
                zIndex: 5,
                fontWeight: 700,
                fontSize: '0.95rem',
                color: '#09090b',
                bgcolor: '#f4f4f5',
                borderRight: '1px solid #e4e4e7',
                borderBottom: 'none',
                py: 2
              }}>
                Итого:
              </TableCell>
              {tableFields.slice(1).map((field) => (
                <TableCell key={field.cName} sx={{
                  color: '#09090b',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  fontFamily: '"Geist Mono", "Roboto Mono", monospace',
                  bgcolor: '#f4f4f5',
                  borderBottom: 'none',
                  py: 2
                }}>
                  {calculateTotals()[field.cName] !== undefined
                    ? formatNumber(calculateTotals()[field.cName])
                    : ''}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Statistics;